import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/seance-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import dropDownFilter from 'src/components/react-table/filters/dropdown-filter';
import { ISeance } from 'src/types/seance';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';

import dateFilter from '../../../../../../../components/react-table/filters/date-filter';

import { IOrder } from '../../../../../../../types/order';
import { getValFromEnum } from '../../../../../../../components/redux-form/utils/dropdown-values';
import { withoutHiddenStatuses } from 'src/helpers/seance/seanceStatus';

import SeanceEnum from 'src/enumerables/seance-enum';
import SeanceActionsColumn from './SeanceActionsColumn';
import { changeToZeroMoment } from '../../../../../../../helpers/timeHelpers';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import Router from '../../../../../../../navigation/router';
import { routes } from '../../../../../../../navigation';
import { numberWithCommas } from '../../../../../../../helpers/format';
import DataSheetLabel from '../../../../../../../components/common/DatasheetLabel';
import user from '../../../../../../../services/user-auth';
import UserRole from '../../../../../../../enumerables/user-role';
import ConfirmInTimeAction from '../../../../ProviderAnalytics/screens/ProviderAnalyticsDetail/components/ConfirmInTimeAction';
import { IServiceType } from '../../../../../../../types/service-types';
import { IServiceLocation } from '../../../../../../../types/service-locations';

interface Props {
    data: Paginated<ISeance>;
    inProgressCSV: boolean;
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
    providers: SelectObject[];
    students: SelectObject[];
    serviceTypes: SelectObject[];
    serviceLocations: SelectObject[];
    districts: SelectObject[];
    vendors: SelectObject[];
    columnOpen: boolean;
    setColumnOpen: (data: boolean) => void;
}

const statusEnum = SeanceEnum ? SeanceEnum.getEnum('SeanceStatus') : {};
const statusItems: SelectObject[] = getValFromEnum(statusEnum);

const dataSheetEnum = SeanceEnum ? SeanceEnum.getEnum('SeanceFilterDatasheet') : {};
const dataSheetItems: SelectObject[] = getValFromEnum(dataSheetEnum);

const getColumns = (
    data: SelectObject[],
    dataStud: SelectObject[],
    dataServiceType: SelectObject[],
    dataServiceLocation: SelectObject[],
    vendors: SelectObject[],
    districts: SelectObject[],
    columnOpen: boolean,
    setColumnOpen: (data: boolean) => void,
): Column<ISeance>[] => {
    const isProvider = user.getRoles().includes(UserRole.ROLE_USER);
    const isSupervisor = user.getSupervisor();
    const isJustProvider = isProvider && !isSupervisor;

    const inTimeFilterValues: SelectObject[] = [
        {
            label: 'yes',
            value: 1,
        },
        {
            label: 'no',
            value: 2,
        },
    ];

    const columnData: Column<ISeance>[] = [
        {
            Header: 'Note ID',
            accessor: 'id', //occasion
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const dataSheet: string = original.datasheet.join(',');
                return isJustProvider && original['signDoc'] && dataSheet !== 'Waiting' ? (
                    <a rel="noopener noreferrer" target="_blank" href={`${original['signDoc']}`}>
                        Note
                    </a>
                ) : original?.provider ? (
                    <Link
                        className={'btn btn-info'}
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            subId: original['id'],
                            id: original?.provider?.id,
                            tab: 'case-sessions-edit',
                        })}
                        target="_blank"
                    >
                        {original['id']}
                    </Link>
                ) : (
                    original['id']
                );
            },
            disableSortBy: true,
            Filter: textFilter<ISeance>(),
        },
        {
            Header: 'Provider',
            accessor: 'provider.id',
            disableSortBy: true,
            Filter: dropDownFilter<ISeance>(data, styles.minWidth),
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                original?.provider ? (
                    <Link
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            id: original?.provider?.id,
                            tab: 'main-info',
                            subId: '',
                        })}
                    >
                        {original?.provider?.fullName}
                    </Link>
                ) : (
                    ''
                ),
        },
        {
            Header: 'Student',
            accessor: 'student.id',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                original?.student ? `${original?.student?.firstName} ${original?.student?.lastName}` : '',
            Filter: dropDownFilter<ISeance>(dataStud, styles.minWidth),
        },
        {
            Header: 'Supervisor',
            accessor: 'supervisor',
            disableSortBy: true,

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (Object.values(original.supervisor).length) {
                    return original.supervisor.fullname;
                }
                return null;
            },
            Filter: dropDownFilter<ISeance>(data, styles.minWidth),
        },

        {
            Header: 'Case',
            accessor: 'occasion.caseNumber',
            disableSortBy: true,
            disableFilters: false,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.occasion) return '';
                return (
                    <Link
                        to={Router.generate(routes.CASE_EDIT, {
                            id: original?.occasion?.id,
                        })}
                    >
                        {original.occasion?.caseNumber}
                    </Link>
                );
            },
            Filter: textFilter<ISeance>(),
        },
        {
            Header: 'Service',
            accessor: 'serviceType.id',
            disableSortBy: true,
            disableFilters: false,
            Filter: dropDownFilter<IServiceType>(dataServiceType, styles.minWidth),
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                let title = original?.serviceType?.title;
                if (!title) {
                    title = original?.serviceType?.cpt;
                }
                return title;
            },
        },
        {
            Header: 'Location',
            accessor: 'serviceLocation.id',
            disableSortBy: true,
            disableFilters: false,
            Filter: dropDownFilter<IServiceLocation>(dataServiceLocation, styles.minWidth),
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const locationArr: string[] = [];
                locationArr.push(original['serviceLocation'] ? original['serviceLocation']?.title : '');
                if (original.isHome) {
                    locationArr.push('[Home - In Person]');
                }
                if (original.isInPerson) {
                    locationArr.push('[School - In Person]');
                }
                if (original.isTelehealth) {
                    locationArr.push('[Telehealth]');
                }
                if (original.isSchool) {
                    locationArr.push('[School - Telehealth]');
                }
                return locationArr.join(', ');
            },
        },
        {
            Header: 'Date',
            accessor: 'seanceDate',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.seanceDate, 'MMM D, YYYY'),
            Filter: dateFilter<ISeance>(),
        },
        {
            Header: 'Start',
            accessor: 'startTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.startTime, 'LT'),
            disableSortBy: true,
            disableFilters: true,
            Filter: dateFilter<ISeance>(),
        },
        {
            Header: 'End',
            accessor: 'endTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => changeToZeroMoment(original.endTime, 'LT'),
            disableSortBy: true,
            disableFilters: true,
            Filter: dateFilter<ISeance>(),
        },
        {
            Header: 'PBook',
            disableSortBy: true,
            accessor: 'datasheet',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const dataSheet: string = original.datasheet.join(',');
                return <DataSheetLabel text={dataSheet} />;
            },
            Filter: dropDownFilter<IOrder>(dataSheetItems, styles.minWidth),
        },
        {
            Header: 'Status',
            disableSortBy: true,
            accessor: 'status',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                const valueCaseStatus = statusItems.find((it) => it.value === original.status);
                return valueCaseStatus ? valueCaseStatus.label : '';
            },
            Filter: dropDownFilter<IOrder>(withoutHiddenStatuses(statusItems), styles.minWidth),
        },
        {
            Header: 'Confirmed On Time',
            disableSortBy: true,
            accessor: 'isConfirmInTime',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                return <ConfirmInTimeAction data={original} />;
            },
            Filter: dropDownFilter<ISeance>(inTimeFilterValues, styles.minWidth),
        },
    ];
    if (columnOpen) {
        columnData.push({
            Header: 'Vendor',
            accessor: 'vendor.id',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.vendor) return '';

                return (
                    <Link
                        to={Router.generate(routes.VENDOR_DETAIL, {
                            id: original?.vendor?.id,
                        })}
                    >
                        {`${original.vendor?.name}`}
                    </Link>
                );
            },
            Filter: dropDownFilter<ISeance>(vendors, styles.minWidth),
        });
        columnData.push({
            Header: 'District',
            disableSortBy: true,

            accessor: 'district.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => {
                if (!original?.district) return '';

                return (
                    <Link
                        to={Router.generate(routes.DISTRICT_DETAIL, {
                            id: original?.district?.id,
                        })}
                    >
                        {`${original.district?.name}`}
                    </Link>
                );
            },
            Filter: dropDownFilter<ISeance>(districts, styles.minWidth),
        });
        columnData.push({
            Header: 'Substituted',
            accessor: 'substitutedProvider.id',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                `${original?.substitutedProvider?.fullName ? original?.substitutedProvider?.fullName : ''}`,
            Filter: dropDownFilter<ISeance>(data, styles.minWidth),
        });
        columnData.push({
            Header: 'Ext.EvalRate',
            accessor: 'externalEvalHourlyRate',

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                original?.externalEvalHourlyRate ? `$${numberWithCommas(original?.externalEvalHourlyRate)}` : '',
            Filter: textFilter<ISeance>(),
            disableSortBy: true,
            disableFilters: true,
        });
        columnData.push({
            Header: 'Ext.Rate',
            accessor: 'externalRatePerHour',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                `${original?.externalRatePerHour ? `$${numberWithCommas(original?.externalRatePerHour)}` : ''}`,
            Filter: textFilter<ISeance>(),
            disableSortBy: true,
            disableFilters: true,
        });
        columnData.push({
            Header: 'Int.Rate',
            accessor: 'internalRatePerHour',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) =>
                `${original?.internalRatePerHour ? `$${numberWithCommas(original?.internalRatePerHour)}` : ''}`,
            Filter: textFilter<ISeance>(),

            disableSortBy: true,
            disableFilters: true,
        });
        columnData.push({
            Header: 'Actions',
            accessor: 'attendance',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => (
                <SeanceActionsColumn setColumn={setColumnOpen} column={columnOpen} original={original} />
            ),
            noDotsRow: true,
            disableSortBy: true,
            disableFilters: true,
            width: '10%',
        });
    } else {
        columnData.push({
            Header: 'Actions',
            accessor: 'attendance',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<ISeance>) => (
                <SeanceActionsColumn setColumn={setColumnOpen} column={columnOpen} original={original} />
            ),
            noDotsRow: true,
            disableSortBy: true,
            disableFilters: true,
            width: '10%',
        });
    }
    return columnData;
};

const SeancesGrid: React.FunctionComponent<Props> = ({
    data,
    vendors,
    columnOpen,
    districts,
    getData,
    providers,
    inProgressCSV,
    getDataCsv,
    students,
    serviceTypes,
    serviceLocations,
    setColumnOpen,
}: Props) => {
    const [columns, setColumns] = useState<any>([]);

    if (providers.length && students.length && serviceTypes.length && serviceLocations.length && !columns.length) {
        setColumns(
            getColumns(
                providers,
                students,
                serviceTypes,
                serviceLocations,
                vendors,
                districts,
                columnOpen,
                setColumnOpen,
            ),
        );
    }
    useEffect(() => {
        if (providers.length && students.length && serviceTypes.length && serviceLocations.length) {
            setColumns(
                getColumns(
                    providers,
                    students,
                    serviceTypes,
                    serviceLocations,
                    vendors,
                    districts,
                    columnOpen,
                    setColumnOpen,
                ),
            );
        }
        // eslint-disable-next-line
    }, [columnOpen]);

    const match: any = useRouteMatch();
    const filtersInit: any = [];
    const locationObject: any = useLocation();
    const query = new URLSearchParams(locationObject.search);

    if (query.get('status')) {
        match.params.status = query.get('status');
    }
    if (query.get('datasheet')) {
        match.params.datasheet = query.get('datasheet');
    }
    filtersInit.push({ id: 'dateNoPeriod', value: '1' });
    useEffect(() => {
        if ((Object.values(match.params).length && match.params.datasheet) || match.params.datasheet) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
        if ((Object.values(match.params).length && match.params.status) || match.params.status) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
    }, []); // eslint-disable-line

    return (
        <div className={classNames(styles.grid)}>
            <Grid<ISeance>
                columnOpen={columnOpen}
                setColumnOpen={setColumnOpen}
                columns={columns}
                inProgressCSV={inProgressCSV}
                data={data}
                getDataCsv={getDataCsv}
                getData={getData}
                filtersInit={filtersInit}
            />
        </div>
    );
};

export default SeancesGrid;
